import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import ScrollToTop from "@/components/ScrollToTop";
import { HeadProvider } from "@/hooks/useHead";
import { HelmetProvider } from "react-helmet-async";
import { lazy, Suspense } from "react";

import Index from "./pages/Index";

// Lazy load listing pages
const ProjectsPage = lazy(() => import("./pages/ProjectsPage"));
const VenturesPage = lazy(() => import("./pages/VenturesPage"));

// Lazy load AI case-study pages
const AiDossier = lazy(() => import("./pages/ai/AiDossier"));
const Robynn = lazy(() => import("./pages/projects/Robynn"));
const HandshakeHub = lazy(() => import("./pages/projects/handshake/HandshakeHub"));
const HandshakeToolPage = lazy(() => import("./pages/projects/handshake/HandshakeToolPage"));
const AlephHub = lazy(() => import("./pages/projects/aleph/AlephHub"));
const AlephCaseStudyPage = lazy(() => import("./pages/projects/aleph/AlephCaseStudyPage"));

// Lazy load project pages
const BlinkMobility = lazy(() => import("./pages/projects/BlinkMobility"));
const NoumenaCaseStudy = lazy(() => import("./pages/projects/NoumenaCaseStudy"));

// Lazy load venture pages
const NatraRosa = lazy(() => import("./pages/venture/NatraRosa"));
const Awaneek = lazy(() => import("./pages/venture/Awaneek"));
const YallaPizza = lazy(() => import("./pages/venture/YallaPizza"));
const Qaf = lazy(() => import("./pages/venture/Qaf"));

const NotFound = lazy(() => import("./pages/NotFound"));
const LoadingFallback = () => (
    <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading...</div>
    </div>
);

const App = () => (
    <HelmetProvider>
        <HeadProvider>
            <TooltipProvider>
                <Toaster />
                <Sonner />
                <BrowserRouter>
                    <ScrollToTop />
                    <Routes>
                        <Route path="/" element={<Index />} />
                        <Route path="/projects" element={
                            <Suspense fallback={<LoadingFallback />}>
                                <ProjectsPage />
                            </Suspense>
                        } />
                        <Route path="/ventures" element={
                            <Suspense fallback={<LoadingFallback />}>
                                <VenturesPage />
                            </Suspense>
                        } />
                        <Route path="/ai" element={
                            <Suspense fallback={<LoadingFallback />}>
                                <AiDossier />
                            </Suspense>
                        } />
                        <Route path="/project/robynn" element={
                            <Suspense fallback={<LoadingFallback />}>
                                <Robynn />
                            </Suspense>
                        } />
                        <Route path="/project/handshake" element={
                            <Suspense fallback={<LoadingFallback />}>
                                <HandshakeHub />
                            </Suspense>
                        } />
                        <Route path="/project/handshake/:slug" element={
                            <Suspense fallback={<LoadingFallback />}>
                                <HandshakeToolPage />
                            </Suspense>
                        } />
                        <Route path="/project/aleph-labs" element={
                            <Suspense fallback={<LoadingFallback />}>
                                <AlephHub />
                            </Suspense>
                        } />
                        <Route path="/project/aleph-labs/:slug" element={
                            <Suspense fallback={<LoadingFallback />}>
                                <AlephCaseStudyPage />
                            </Suspense>
                        } />
                        {/* Redirects for the former standalone bank pages (in-app fallback; firebase.json does the 301). */}
                        <Route path="/project/cimb-bank-app" element={<Navigate to="/project/aleph-labs/cimb" replace />} />
                        <Route path="/project/rhb-bank-app" element={<Navigate to="/project/aleph-labs/rhb" replace />} />
                        <Route path="/project/ambank-app" element={<Navigate to="/project/aleph-labs/ambank" replace />} />
                        <Route path="/project/in1bank-app" element={<Navigate to="/project/aleph-labs/in1bank" replace />} />
                        <Route path="/project/blink-mobility" element={
                            <Suspense fallback={<LoadingFallback />}>
                                <BlinkMobility />
                            </Suspense>
                        } />
                        <Route path="/project/noumena-freelancers" element={
                            <Suspense fallback={<LoadingFallback />}>
                                <NoumenaCaseStudy />
                            </Suspense>
                        } />
                        <Route path="/venture/natrarosa" element={
                            <Suspense fallback={<LoadingFallback />}>
                                <NatraRosa />
                            </Suspense>
                        } />
                        <Route path="/venture/awaneek" element={
                            <Suspense fallback={<LoadingFallback />}>
                                <Awaneek />
                            </Suspense>
                        } />
                        <Route path="/venture/yalla-pizza" element={
                            <Suspense fallback={<LoadingFallback />}>
                                <YallaPizza />
                            </Suspense>
                        } />
                        <Route path="/venture/qaf" element={
                            <Suspense fallback={<LoadingFallback />}>
                                <Qaf />
                            </Suspense>
                        } />
                        <Route path="*" element={
                            <Suspense fallback={<LoadingFallback />}>
                                <NotFound />
                            </Suspense>
                        } />
                    </Routes>
                </BrowserRouter>
            </TooltipProvider>
        </HeadProvider>
    </HelmetProvider>
);

export default App;
